/**
 * Quick-chat PDF export (§21). Two self-contained, offline print-to-PDF affordances for
 * the quick-chat panel — mirroring the research-report export (`lib/reportExport.ts`):
 *
 *  - Session PDF: the whole current conversation → a branded, print-styled document.
 *  - Message PDF: a single assistant message (its prose + reasoning, if present) → PDF.
 *
 * Both render the transcript data the panel already holds (`TranscriptItem`s) into a
 * standalone `<!doctype html>` document with an inline print stylesheet, then hand it to
 * the shared {@link printHtmlDocument} (browser print dialog → "Save as PDF"). This keeps
 * the export dependency-free, fully offline (no CDN), and consistent with the report
 * export — deliberately reusing its `escapeHtml`/`markdownToHtml`/print mechanism so the
 * two PDFs share one code path and one visual house style.
 *
 * The HTML-building functions (`transcriptToPrintHtml`, `messageToPrintHtml`) are pure and
 * deterministic, so a sample conversation / message can be asserted directly in a unit
 * test with `window.print` stubbed.
 */
import type { TranscriptItem } from './chatTranscript';
import { escapeHtml, markdownToHtml, printHtmlDocument } from './reportExport';

export interface ChatPdfOptions {
  sessionId?: string | null;
  /** ISO date (YYYY-MM-DD) stamped in the document header; defaults to today. */
  date?: string;
}

/** Terminal-status note text, mirroring the panel's terminal render. */
const TERMINAL_NOTE: Record<'failed' | 'limit_reached', string> = {
  failed: 'The assistant hit an error and stopped.',
  limit_reached: 'The assistant reached this turn’s budget and stopped.',
};

/**
 * The inline print stylesheet shared by the session + message documents. Matches the
 * research-report export's typography on purpose so both PDFs read as one house style;
 * turns avoid page breaks mid-block for a clean multi-page result.
 */
const CHAT_PRINT_STYLES = `
  html, body { margin: 0; padding: 0; }
  body {
    font-family: "Inter", system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #141414; line-height: 1.5; padding: 32px 40px;
  }
  h1 { font-size: 24px; font-weight: 700; margin: 0 0 4px; letter-spacing: -0.01em; }
  h2 { font-size: 19px; font-weight: 600; margin: 24px 0 8px; }
  h3 { font-size: 16px; font-weight: 600; margin: 18px 0 6px; }
  p { margin: 0 0 10px; }
  ul, ol { margin: 0 0 10px; padding-left: 24px; }
  li { margin: 2px 0; }
  a { color: #005ff3; }
  code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; background: #f5f5f5; padding: 1px 4px; border-radius: 3px; }
  .meta { color: #6b6b6b; font-size: 12px; margin: 0 0 2px; }
  .turn { margin: 0 0 18px; page-break-inside: avoid; }
  .role { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; margin: 0 0 4px; }
  .turn.user .role { color: #005ff3; }
  .turn.assistant .role { color: #141414; }
  .user-text { white-space: pre-wrap; margin: 0; }
  .reasoning {
    border-left: 2px solid #d8d8d8; padding: 2px 0 2px 12px; margin: 0 0 8px;
    color: #6b6b6b; font-size: 13px; white-space: pre-wrap;
  }
  .reasoning .reasoning-label { font-style: italic; font-weight: 600; }
  .tool { color: #6b6b6b; font-size: 13px; margin: 0 0 12px; }
  .tool code, .approval code { background: #f0f0f0; }
  .approval { color: #8a6d00; font-size: 13px; margin: 0 0 12px; }
  .note { color: #b00020; font-size: 13px; margin: 4px 0 0; }
  .note.limit { color: #8a6d00; }
  @page { margin: 18mm; }
`;

/**
 * Condense message text into a short, filename-safe phrase for a document title.
 * Strips Markdown/URL noise, collapses whitespace, keeps the first few words, and
 * trims trailing punctuation — so the browser's "Save as PDF" filename reads like
 * the conversation ("EHE chat — Draft a reply to Acme…") instead of a generic name.
 */
function summarizeForTitle(text: string, maxWords = 8, maxChars = 64): string {
  const flat = text
    .replace(/```[\s\S]*?```/g, ' ') // fenced code blocks
    .replace(/`[^`]*`/g, ' ') // inline code
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // [text](url) → text
    .replace(/https?:\/\/\S+/g, ' ') // bare URLs
    .replace(/[*_#>`<]/g, ' ') // emphasis/heading/angle marks
    .replace(/\s+/g, ' ')
    .trim();
  if (!flat) return '';
  let phrase = flat.split(' ').slice(0, maxWords).join(' ');
  if (phrase.length > maxChars) phrase = `${phrase.slice(0, maxChars).trimEnd()}…`;
  return phrase.replace(/[.,;:!?]+$/, '').trim();
}

/** Descriptive document title for a whole conversation — drives the PDF filename. */
function sessionDocTitle(items: TranscriptItem[], date: string): string {
  const firstUser = items.find((i): i is Extract<TranscriptItem, { kind: 'user' }> => i.kind === 'user');
  const summary = firstUser ? summarizeForTitle(firstUser.content) : '';
  return summary ? `EHE chat — ${summary} — ${date}` : `EHE chat — ${date}`;
}

/** Descriptive document title for a single assistant message — drives the PDF filename. */
function messageDocTitle(item: Extract<TranscriptItem, { kind: 'assistant' }>, date: string): string {
  const summary = summarizeForTitle(item.content);
  return summary ? `EHE reply — ${summary} — ${date}` : `EHE reply — ${date}`;
}

/** Wrap rendered body HTML in a complete, self-contained print document. */
function printDocument(title: string, bodyHtml: string): string {
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>${CHAT_PRINT_STYLES}</style>
</head>
<body>${bodyHtml}</body>
</html>`;
}

/** Render an assistant reasoning summary (if any) as a "Thinking" aside. */
function reasoningHtml(reasoning: string | undefined): string {
  if (!reasoning || !reasoning.trim()) return '';
  return `<div class="reasoning"><span class="reasoning-label">Thinking:</span> ${escapeHtml(reasoning.trim())}</div>`;
}

/** Render one assistant transcript item's body (reasoning + prose + terminal note). */
function assistantBodyHtml(item: Extract<TranscriptItem, { kind: 'assistant' }>): string {
  const parts: string[] = [reasoningHtml(item.reasoning)];
  const content = item.content.trim();
  if (content) parts.push(markdownToHtml(content));
  if (item.status && item.status !== 'completed') {
    const cls = item.status === 'limit_reached' ? 'note limit' : 'note';
    parts.push(`<p class="${cls}">${escapeHtml(item.error ?? TERMINAL_NOTE[item.status])}</p>`);
  }
  return parts.filter(Boolean).join('\n');
}

/**
 * Build a standalone HTML document for the whole conversation — title + meta header, then
 * each item as a You / Assistant turn (with tool-call + approval + reasoning lines like the
 * Markdown export), ready to print to PDF. Pure + deterministic.
 */
export function transcriptToPrintHtml(items: TranscriptItem[], opts: ChatPdfOptions = {}): string {
  const date = opts.date ?? new Date().toISOString().slice(0, 10);
  const header: string[] = ['<h1>Assistant conversation</h1>'];
  if (opts.sessionId) header.push(`<p class="meta">Session: ${escapeHtml(opts.sessionId)}</p>`);
  header.push(`<p class="meta">Exported: ${escapeHtml(date)}</p>`);

  const body = items
    .map((item) => {
      switch (item.kind) {
        case 'user':
          return `<div class="turn user"><p class="role">You</p><p class="user-text">${escapeHtml(item.content)}</p></div>`;
        case 'assistant':
          return `<div class="turn assistant"><p class="role">Assistant</p>${assistantBodyHtml(item)}</div>`;
        case 'tool_call':
          return `<p class="tool">🔧 called <code>${escapeHtml(item.tool)}</code></p>`;
        case 'approval':
          return `<p class="approval">⏸️ approval requested for <code>${escapeHtml(item.tool)}</code></p>`;
        default:
          return '';
      }
    })
    .filter(Boolean)
    .join('\n');

  return printDocument(sessionDocTitle(items, date), `${header.join('\n')}\n${body}`);
}

/**
 * Build a standalone HTML document for a single assistant message — its reasoning (if
 * present) and prose. Pure + deterministic.
 */
export function messageToPrintHtml(
  item: Extract<TranscriptItem, { kind: 'assistant' }>,
  opts: ChatPdfOptions = {},
): string {
  const date = opts.date ?? new Date().toISOString().slice(0, 10);
  const header = [
    '<h1>Assistant message</h1>',
    `<p class="meta">Exported: ${escapeHtml(date)}</p>`,
  ];
  const body = `<div class="turn assistant">${assistantBodyHtml(item)}</div>`;
  return printDocument(messageDocTitle(item, date), `${header.join('\n')}\n${body}`);
}

/** Render the conversation to a print document and open the browser print → Save-as-PDF. */
export function printChatSessionAsPdf(items: TranscriptItem[], opts: ChatPdfOptions = {}): void {
  printHtmlDocument(transcriptToPrintHtml(items, opts));
}

/** Render a single assistant message to a print document and open print → Save-as-PDF. */
export function printChatMessageAsPdf(
  item: Extract<TranscriptItem, { kind: 'assistant' }>,
  opts: ChatPdfOptions = {},
): void {
  printHtmlDocument(messageToPrintHtml(item, opts));
}
